import React, { FC, Fragment, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import * as PT from 'modules/Pricing/types'

import LinkText from 'design-system/Links/LinkText'

import RoundedCheckmarkCircleFilled from 'components/SVG/RoundedCheckmarkCircleFilled'
import Spacer from 'components/Spacer'

import * as T from 'types'
import * as E from 'types/enums'

const LIVE_TOURS_APP = 'Live Tours App'

const Container = styled.div<PT.IPricingPackageCardModeProps>`
  padding: ${props => props.theme.spacings[16]}px 0 0;
  background-color: ${props =>
    props.mode === E.PackageCardMode.Best ? props.theme.colors.blue900 : props.theme.colors.white};

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    padding: 0;
  }
`

interface IGetColorArgs extends PT.IPricingPackageCardModeProps {
  theme: any
}
const getColor = (props: IGetColorArgs, isDesktop = false) => {
  if (props.mode === E.PackageCardMode.Best) return props.theme.colors.white

  if (props.mode === E.PackageCardMode.ComingSoon) return props.theme.colors.neutral300

  if (isDesktop) return props.theme.colors.neutral600

  return props.theme.colors.neutral400
}

const Header = styled.div<PT.IPricingPackageCardModeProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 33px;
  font-weight: ${props => props.theme.fonts.weights.heavy};
  font-size: ${props => props.theme.fonts.sizes[16]}px;
  line-height: 22px;

  color: ${props => props.theme.colors.grey33};
  background-color: ${props => props.theme.colors.grey31};

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    display: block;
    align-items: none;
    justify-content: none;
    height: auto;
    color: ${props => getColor(props, true)};
    background-color: transparent;
  }
`

const List = styled.div``

const ListItem = styled.div<PT.IPricingPackageCardModeProps>`
  display: flex;
  font-weight: ${props => props.theme.fonts.weights.medium};
  font-size: ${props => props.theme.fonts.sizes[14]}px;
  line-height: 20px;
  color: ${props => getColor(props)};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: ${props => props.theme.fonts.sizes[16]}px;
    line-height: 22px;
  }
`

const NoWrapSpan = styled.span`
  white-space: nowrap;
`

const AsteriskAnchor = styled.a`
  text-decoration: none;
  color: ${props => props.theme.colors.blue500};
  font-weight: 800;
  cursor: pointer;
`

const CheckmarkIconContainer = styled.div`
  width: 16px;
  height: 16px;
`

interface IProps extends Partial<PT.IPricingPackageCardModeProps> {
  features: T.IPricingPackageItem[]
  featureType: E.FeatureType
  packageType?: E.PackageType
}

const Features: FC<IProps> = ({ mode, features, featureType, packageType }) => {
  const theme = useContext(ThemeContext)

  const getFeatureList = () => {
    const includedFeatures = features.filter(feature => feature.included)

    switch (featureType) {
      case E.FeatureType.help:
        return includedFeatures.filter(feature => feature.type === E.FeatureType.help)
      case E.FeatureType.support:
        return includedFeatures.filter(feature => feature.type === E.FeatureType.support)
      default:
        return includedFeatures.filter(feature => feature.type === E.FeatureType.service)
    }
  }

  const getTitle = () => {
    if (!packageType) {
      if (featureType === E.FeatureType.service) return "What You'll Get:"

      if (featureType === E.FeatureType.help) return "How We'll Help:"

      return 'Support Available:'
    }

    return packageType
  }

  const getCheckmarkFillColor = (cardMode: E.PackageCardMode | undefined) => {
    if (cardMode === E.PackageCardMode.Best) return theme.colors.transparentWhiteAlpha10

    if (cardMode === E.PackageCardMode.ComingSoon) return theme.colors.neutral25

    return theme.colors.transparentTealAlpha75
  }

  const getCheckmarkColor = (cardMode: E.PackageCardMode | undefined) => {
    if (cardMode === E.PackageCardMode.Best) return theme.colors.white

    if (cardMode === E.PackageCardMode.ComingSoon) return theme.colors.neutral300

    return theme.colors.blue500
  }

  const title = getTitle()
  return (
    <Container mode={mode}>
      <Header mode={mode}>{title}</Header>
      <Spacer
        size={16}
        desktopSize={10}
      />
      <List>
        {getFeatureList().map((feature, index) => {
          const featureSplitText = feature.name.split(' ')
          const featureSplitLastWord = featureSplitText.pop()
          const featureTextJoined = featureSplitText.join(' ')
          return (
            <Fragment key={feature._id}>
              <ListItem mode={mode}>
                <CheckmarkIconContainer>
                  <RoundedCheckmarkCircleFilled
                    width="16px"
                    fill={getCheckmarkFillColor(mode)}
                    checkmarkColor={getCheckmarkColor(mode)}
                  />
                </CheckmarkIconContainer>
                <Spacer size={12} />
                {/*
                  @TODO
                  There's currently only a single feature that requires a link. If more features
                  require links, we should refactor PricingPackageItem to include a link property
                  and LinkText to allow for React children
                */}
                {feature.name === LIVE_TOURS_APP
                  ? (
                    <NoWrapSpan>
                      <LinkText
                        href="/virtual-home-tours"
                        text={feature.name}
                        type="Body1"
                      />{' '}
                      {feature.disclaimer && (
                        <AsteriskAnchor href={`#${feature.disclaimer.replace(/\s+/g, '-')}`}>
                        *
                        </AsteriskAnchor>
                      )}
                    </NoWrapSpan>
                  )
                  : (
                    <span>
                      {featureTextJoined}{' '}
                      <NoWrapSpan>
                        {featureSplitLastWord}
                        {feature.disclaimer && (
                          <span>
                            {' '}
                            <AsteriskAnchor href={`#${feature.disclaimer.replace(/\s+/g, '-')}`}>
                            *
                            </AsteriskAnchor>
                          </span>
                        )}
                      </NoWrapSpan>
                    </span>
                  )}
              </ListItem>
              {index < getFeatureList().length - 1 && <Spacer size={10} />}
            </Fragment>
          )
        })}
      </List>
    </Container>
  )
}

export default Features
